import React, { useState, useEffect } from 'react';
import { useColorTheme } from '../../context/ColorThemeContext';

const SuccessCelebrationModal = ({ 
  isOpen, 
  onClose, 
  title, 
  message, 
  icon = "🎉", 
  actionButton = null,
  celebrationType = "success" // success, achievement, milestone
}) => {
  const { colorTheme } = useColorTheme();
  const [stage, setStage] = useState('entering'); // entering, celebrating, exiting
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setStage('entering');
      
      // Generate celebration particles
      const newParticles = Array.from({ length: 25 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        delay: Math.random() * 0.6,
        duration: 1.5 + Math.random() * 2,
        color: ['#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#10b981', '#f97316'][Math.floor(Math.random() * 6)]
      }));
      setParticles(newParticles);

      // Animation sequence
      const timer1 = setTimeout(() => setStage('celebrating'), 400);
      const timer2 = setTimeout(() => setStage('exiting'), 3500);
      const timer3 = setTimeout(() => {
        setStage('complete');
        onClose?.();
      }, 4000);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    }
  }, [isOpen, onClose]);

  const getCelebrationGradient = (type) => {
    const gradients = colorTheme === 'purple' ? {
      success: 'from-purple-400 via-purple-500 to-purple-600',
      achievement: 'from-yellow-400 via-yellow-500 to-orange-500',
      milestone: 'from-purple-400 via-purple-500 to-pink-600',
      sharing: 'from-purple-400 via-purple-500 to-pink-600',
      publishing: 'from-purple-400 via-purple-500 to-pink-600'
    } : colorTheme === 'blue' ? {
      success: 'from-blue-400 via-blue-500 to-blue-600',
      achievement: 'from-yellow-400 via-yellow-500 to-orange-500',
      milestone: 'from-blue-400 via-sky-500 to-blue-600',
      sharing: 'from-blue-400 via-sky-500 to-blue-600',
      publishing: 'from-blue-400 via-sky-500 to-blue-600'
    } : {
      success: 'from-green-400 via-green-500 to-green-600',
      achievement: 'from-yellow-400 via-yellow-500 to-orange-500',
      milestone: 'from-purple-400 via-purple-500 to-purple-600',
      sharing: 'from-blue-400 via-blue-500 to-blue-600',
      publishing: 'from-orange-400 via-orange-500 to-red-500'
    };
    return gradients[type] || gradients.success;
  };

  const getIconByType = (type) => {
    const icons = {
      success: '🎉',
      achievement: '🏆',
      milestone: '⭐',
      sharing: '🚀',
      publishing: '📢'
    };
    return icons[type] || icon;
  };

  if (!isOpen || stage === 'complete') return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
      {/* Celebration Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className={`absolute w-3 h-3 rounded-full opacity-0 ${
              stage === 'celebrating' ? 'animate-bounce' : ''
            }`}
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              backgroundColor: particle.color,
              animationDelay: `${particle.delay}s`,
              animationDuration: `${particle.duration}s`,
              animationFillMode: 'forwards',
              opacity: stage === 'celebrating' ? 1 : 0,
              transition: 'opacity 0.5s ease-in-out',
              boxShadow: '0 0 6px rgba(255, 255, 255, 0.5)'
            }}
          />
        ))}
      </div>

      {/* Enhanced Confetti Effect */}
      {stage === 'celebrating' && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {Array.from({ length: 60 }, (_, i) => (
            <div
              key={`confetti-${i}`}
              className="absolute w-2 h-4 animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `-10px`,
                backgroundColor: ['#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#10b981', '#f97316'][i % 6],
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: '3s',
                transform: `rotate(${Math.random() * 360}deg)`,
                animation: `confettiFall 3s linear ${Math.random() * 2}s forwards`
              }}
            />
          ))}
        </div>
      )}

      {/* Main Celebration Card */}
      <div className={`relative transform transition-all duration-700 ease-out ${
        stage === 'entering' ? 'scale-0 rotate-12 opacity-0' :
        stage === 'celebrating' ? 'scale-100 rotate-0 opacity-100' :
        'scale-95 opacity-0'
      }`}>
        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 max-w-md mx-4 border-4 border-yellow-400 relative overflow-hidden">
          {/* Background glow effect */}
          <div className={`absolute inset-0 bg-gradient-to-br rounded-3xl ${colorTheme === 'purple' ? 'from-purple-100 via-pink-50 to-purple-50 dark:from-purple-900/20 dark:via-pink-900/20 dark:to-purple-900/20' : colorTheme === 'blue' ? 'from-blue-100 via-sky-50 to-blue-50 dark:from-blue-900/20 dark:via-sky-900/20 dark:to-blue-900/20' : 'from-yellow-100 via-orange-50 to-green-50 dark:from-yellow-900/20 dark:via-orange-900/20 dark:to-green-900/20'}`}></div>
          
          {/* Header */}
          <div className="relative text-center mb-6">
            <div className="inline-flex items-center space-x-3 mb-3">
              <div className="text-5xl animate-bounce">{getIconByType(celebrationType)}</div>
              <h2 className={`text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-500 via-pink-500 to-purple-600' : colorTheme === 'blue' ? 'from-blue-500 via-sky-500 to-blue-600' : 'from-yellow-500 via-orange-500 to-green-500'}`}>
                {title || "Success!"}
              </h2>
              <div className="text-5xl animate-bounce" style={{ animationDelay: '0.2s' }}>✨</div>
            </div>
          </div>

          {/* Celebration Icon */}
          <div className="relative flex justify-center mb-6">
            <div className={`relative w-24 h-24 rounded-full flex items-center justify-center text-4xl bg-gradient-to-br ${getCelebrationGradient(celebrationType)} text-white shadow-2xl ${
              stage === 'celebrating' ? 'animate-pulse' : ''
            }`}>
              {getIconByType(celebrationType)}
              
              {/* Enhanced glow effects */}
              <div className={`absolute inset-0 rounded-full bg-gradient-to-br ${getCelebrationGradient(celebrationType)} opacity-40 ${
                stage === 'celebrating' ? 'animate-ping' : ''
              }`}></div>
              
              {/* Ring effects */}
              <div className={`absolute -inset-3 rounded-full border-3 border-yellow-400 ${
                stage === 'celebrating' ? 'animate-spin' : ''
              }`}></div>
            </div>
          </div>

          {/* Message */}
          <div className="relative text-center mb-6">
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-xl p-4 mb-4 border-2 border-yellow-200 dark:border-yellow-700">
              <p className="text-base font-medium text-gray-700 dark:text-gray-300 leading-relaxed">
                {message}
              </p>
            </div>
          </div>

          {/* Action Button */}
          <div className="relative text-center">
            {actionButton ? (
              <div className="space-y-3">
                {actionButton}
                <button
                  onClick={onClose}
                  className="block w-full px-6 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-500 transition-all duration-200"
                >
                  Continue
                </button>
              </div>
            ) : (
              <button
                onClick={onClose}
                className={`px-8 py-3 bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-500 via-pink-500 to-purple-600 hover:from-purple-600 hover:via-pink-600 hover:to-purple-700' : colorTheme === 'blue' ? 'from-blue-500 via-sky-500 to-blue-600 hover:from-blue-600 hover:via-sky-600 hover:to-blue-700' : 'from-yellow-500 via-orange-500 to-green-500 hover:from-yellow-600 hover:via-orange-600 hover:to-green-600'} text-white rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl`}
              >
                Awesome! 🚀
              </button>
            )}
          </div>
        </div>

        {/* Enhanced Sparkle Effects */}
        {stage === 'celebrating' && (
          <div className="absolute inset-0 pointer-events-none">
            {Array.from({ length: 10 }, (_, i) => (
              <div
                key={`sparkle-${i}`}
                className="absolute text-yellow-400 text-2xl animate-ping"
                style={{
                  left: `${[10, 90, 20, 80, 50, 30, 70, 60, 15, 85][i]}%`,
                  top: `${[10, 20, 80, 90, 50, 30, 70, 40, 60, 25][i]}%`,
                  animationDelay: `${i * 0.2}s`,
                  animationDuration: '2s'
                }}
              >
                {['✨', '🌟', '💫', '⭐', '🎊'][i % 5]}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Custom CSS for confetti animation */}
      <style jsx>{`
        @keyframes confettiFall {
          0% {
            transform: translateY(-100vh) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default SuccessCelebrationModal;